<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-container">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Đang tải dữ liệu...</p>
      </div>
      <!-- HEADER -->
      <div class="modal-header">
        <h2>Chi tiết khách hàng</h2>
        <button @click="$emit('close')">✖</button>
      </div>

      <!-- TAB -->
      <div class="tabs">
        <button
          :class="{ active: activeTab === 'info' }"
          @click="activeTab = 'info'"
        >
          Thông tin
        </button>
        <button
          :class="{ active: activeTab === 'orders' }"
          @click="activeTab = 'orders'"
        >
          Lịch sử mua hàng
        </button>
      </div>

      <!-- BODY -->
      <div class="modal-body">
        <!-- TAB: THÔNG TIN -->
        <div v-if="activeTab === 'info'" class="info-grid">
          <div><strong>Mã KH:</strong> {{ customer.ma_khach_hang }}</div>
          <div><strong>Tên:</strong> {{ customer.ten_khach_hang }}</div>
          <div><strong>Điện thoại:</strong> {{ customer.dien_thoai }}</div>
          <div><strong>Email:</strong> {{ customer.email }}</div>
          <div><strong>Địa chỉ:</strong> {{ customer.dia_chi || "-" }}</div>
          <div>
            <strong>Nhóm KH:</strong> {{ customer.nhom_khach_hang || "-" }}
          </div>
          <div><strong>Công ty:</strong> {{ customer.cong_ty || "-" }}</div>
          <div><strong>MST:</strong> {{ customer.ma_so_thu || "-" }}</div>
          <div>
            <strong>Ngày tạo:</strong> {{ formatDate(customer.ngay_tao) }}
          </div>
          <div>
            <strong>Trạng thái:</strong>
            <span :class="['status', formatStatus(customer.trang_thai).class]">
              {{ formatStatus(customer.trang_thai).text }}
            </span>
          </div>
        </div>

        <!-- TAB: LỊCH SỬ MUA HÀNG -->
        <div v-if="activeTab === 'orders'">
          <table class="order-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Mã đơn</th>
                <th>Ngày</th>
                <th>Tổng tiền</th>
                <th>Trạng thái</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(order, index) in orders" :key="order.id">
                <td>{{ index + 1 }}</td>
                <td class="details" @click="openInvoiceDetail(order)">
                  {{ order.ma_hoa_don }}
                </td>
                <td>{{ formatDate(order.thoi_gian) }}</td>
                <td>{{ formatCurrency(order.khach_can_tra) }}</td>
                <td>
                  <span
                    :class="[
                      'status',
                      formatStatusInvoices(order.trang_thai).class,
                    ]"
                  >
                    {{ formatStatusInvoices(order.trang_thai).text }}
                  </span>
                </td>
              </tr>

              <tr v-if="!orders.length">
                <td colspan="100%" class="empty">Không có lịch sử mua hàng</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- FOOTER -->
      <div class="modal-footer">
        <button @click="$emit('close')">Đóng</button>
      </div>
    </div>
    <InvoiceDetailModal
      v-if="showInvoiceDetail"
      :invoice="selectedInvoice"
      @close="showInvoiceDetail = false"
    />
  </div>
</template>
<script setup>
import { ref, toRefs, watch } from "vue";
import { adminApi } from "@/services/api";
import { useToast } from "@/composables/useToast";
import { formatCurrency, formatDate } from "@/utils/helpers";
import InvoiceDetailModal from "../invoices/InvoiceDetailModal.vue";

const props = defineProps({
  customer: {
    type: Object,
    required: true,
  },
});
const { customer } = toRefs(props);
const loading = ref(false);
const orders = ref([]);
const activeTab = ref("info");
const toast = useToast();
const formatStatusInvoices = (status) => {
  const statusMap = {
    "Hoàn thành": { text: "Hoàn thành", class: "completed" },
    "Đã huỷ": { text: "Đã huỷ", class: "cancel" },
  };

  return statusMap[status] || { text: "-", class: "status-unknown" };
};
const formatStatus = (status) => {
  const statusMap = {
    1: { text: "Hoạt động", class: "status-active" },
    0: { text: "Tạm ngưng", class: "status-inactive" },
  };

  return statusMap[status] || { text: "-", class: "unknown" };
};
watch(
  () => activeTab.value,
  async (tab) => {
    if (tab === "orders" && customer.value.id) {
      try {
        const response = await adminApi.getInvoicesWithCustomer(
          customer.value.id,
        );
        orders.value = response.data.data.data || response.data;
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "Lỗi khi tải lịch sử mua hàng";
        toast.error(errorMessage);
      }
    }
  },
  { immediate: true },
);

const showInvoiceDetail = ref(false);
const selectedInvoice = ref(null);
const openInvoiceDetail = (invoice) => {
  selectedInvoice.value = invoice;
  showInvoiceDetail.value = true;
};
</script>
<style scoped>
.tabs {
  display: flex;
  border-bottom: 1px solid #ddd;
}

.tabs button {
  padding: 10px 20px;
  border: none;
  background: none;
  cursor: pointer;
}

.tabs button.active {
  border-bottom: 2px solid #42b983;
  font-weight: bold;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.order-table {
  width: 100%;
  border-collapse: collapse;
}

.order-table th,
.order-table td {
  border: 1px solid #ddd;
  padding: 8px;
}
.status {
  padding: 4px 8px;
  border-radius: 5px;
}

.status.completed {
  background: #d4edda;
  color: green;
}

.status.pending {
  background: #fff3cd;
  color: orange;
}
.status.cancel {
  background: #f8d7da;
  color: red;
}
.details {
  color: #42b983;
  cursor: pointer;
}
</style>
